// API helpers for the Field Activities workspace.
// Thin wrappers over the shared apiRequest helpers in ../api.

import { apiGet, apiPatch, apiPost, getAuthToken } from './api';
import { API_URL } from '../api/config';
import { buildCropOptions, normalizeCropName } from './cropCatalog';
import type {
    ActivitiesOverview,
    ActivityReport,
    ActivityType,
    Assignment,
    AssignmentDetail,
    FarmerOption,
    FarmField,
    ReportDetail,
    StaffUser,
} from '../components/activities/types';

interface ListResponse<T> {
    success?: boolean;
    count?: number;
    assignments?: T[];
    reports?: T[];
    summary?: any;
}

type AnyRecord = Record<string, any>;

export interface ActivityMaterialOption {
    id: string;
    name: string;
    unit: string;
    availableQuantity?: number;
    source: 'compost_batch' | 'compost_source';
    status?: string;
    subtitle?: string;
}

const emptyList = <T>(data: any, key: string): T[] =>
    Array.isArray(data) ? data : Array.isArray((data as any)?.[key]) ? (data as any)[key] : [];

const firstList = <T>(data: any, keys: string[]): T[] => {
    if (Array.isArray(data)) return data;
    for (const key of keys) {
        if (Array.isArray(data?.[key])) return data[key];
    }
    return [];
};

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------
export const fetchOverview = async (): Promise<ActivitiesOverview> => {
    const data = await apiGet<any>('/api/activities/overview');
    return data?.overview || { summary: { assignments: {}, reports: {} }, recent_assignments: [], priority_queue: [], observation_alerts: [], top_fields: [] };
};

// ---------------------------------------------------------------------------
// Assignments
// ---------------------------------------------------------------------------
export const fetchAssignments = async (params?: AnyRecord): Promise<Assignment[]> => {
    const data = await apiGet<ListResponse<Assignment>>('/api/activities/assignments', params);
    return data?.assignments || [];
};

export const fetchAssignment = async (id: number): Promise<AssignmentDetail> => {
    const data = await apiGet<any>(`/api/activities/assignments/${id}`);
    return {
        assignment: data?.assignment || null,
        reports: data?.reports || [],
        reviews: data?.reviews || [],
        observations: data?.observations || [],
        materials: data?.materials || [],
    };
};

export const createAssignment = async (payload: AnyRecord): Promise<Assignment> => {
    const data = await apiPost<any>('/api/activities/assignments', payload);
    return data?.assignment;
};

export interface BulkAssignmentEntry {
    farmer_id: number;
    field_plot_id?: number | null;
    [key: string]: any;
}

export const createBulkAssignments = async (payload: AnyRecord & { farmers: BulkAssignmentEntry[] }): Promise<Assignment[]> => {
    const data = await apiPost<any>('/api/activities/assignments/bulk', payload);
    return data?.assignments || [];
};

export const updateAssignment = async (id: number, payload: AnyRecord): Promise<Assignment> => {
    const data = await apiPatch<any>(`/api/activities/assignments/${id}`, payload);
    return data?.assignment;
};

export const publishAssignment = async (id: number, body?: AnyRecord): Promise<any> => {
    return apiPost<any>(`/api/activities/assignments/${id}/publish`, body || {});
};

export const cancelAssignment = async (id: number, reason?: string): Promise<Assignment> => {
    const data = await apiPost<any>(`/api/activities/assignments/${id}/cancel`, { reason: reason || 'Cancelled from dashboard.' });
    return data?.assignment;
};

export const closeAssignment = async (id: number): Promise<Assignment> => {
    const data = await apiPost<any>(`/api/activities/assignments/${id}/close`, {});
    return data?.assignment;
};

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------
export const fetchReports = async (params?: AnyRecord): Promise<ActivityReport[]> => {
    const data = await apiGet<ListResponse<ActivityReport>>('/api/activities/reports', params);
    return data?.reports || [];
};

export const fetchReport = async (id: number): Promise<ReportDetail> => {
    const data = await apiGet<any>(`/api/activities/reports/${id}`);
    // planned_materials / reported_materials are the lot-level evidence used by
    // the review drawer; they stay empty before migration 079 is applied.
    return {
        report: data?.report || null,
        observations: data?.observations || [],
        reviews: data?.reviews || [],
        planned_materials: data?.planned_materials || [],
        reported_materials: data?.reported_materials || [],
    };
};

export const approveReport = async (id: number, comment?: string): Promise<ActivityReport> => {
    const data = await apiPost<any>(`/api/activities/reports/${id}/approve`, { review_comment: comment || null });
    return data?.report;
};

export const requestReportCorrection = async (id: number, instructions: string): Promise<ActivityReport> => {
    const data = await apiPost<any>(`/api/activities/reports/${id}/request-correction`, { correction_instructions: instructions });
    return data?.report;
};

export const rejectReport = async (id: number, reason: string): Promise<ActivityReport> => {
    const data = await apiPost<any>(`/api/activities/reports/${id}/reject`, { rejection_reason: reason });
    return data?.report;
};

// ---------------------------------------------------------------------------
// Activity types
// ---------------------------------------------------------------------------
export const fetchActivityTypes = async (): Promise<ActivityType[]> => {
    const data = await apiGet<any>('/api/activities/types');
    return firstList<ActivityType>(data, ['types', 'activity_types', 'activityTypes', 'data']);
};

export const saveActivityType = async (payload: AnyRecord): Promise<ActivityType> => {
    const data = await apiPost<any>('/api/activities/types', payload);
    return data?.type || data?.activity_type || data?.activityType || data;
};

// ---------------------------------------------------------------------------
// Supporting reference data
// ---------------------------------------------------------------------------
export const fetchFarmers = async (): Promise<FarmerOption[]> => {
    try {
        const data = await apiGet<any>('/api/farmers');
        return firstList<FarmerOption>(data, ['farmers', 'data', 'rows']);
    } catch {
        return [];
    }
};

export const fetchFarmPlots = async (): Promise<FarmField[]> => {
    try {
        const data = await apiGet<any>('/api/maps/farm-plots');
        return firstList<FarmField>(data, ['plots', 'farm_plots', 'farmPlots', 'fields', 'data']);
    } catch {
        return [];
    }
};

export const fetchStaffUsers = async (): Promise<StaffUser[]> => {
    try {
        const data = await apiGet<any>('/api/users');
        return firstList<StaffUser>(data, ['users', 'data', 'rows']);
    } catch {
        return [];
    }
};

export const fetchCropOptions = async (): Promise<string[]> => {
    try {
        const data = await apiGet<any>('/api/crops', { page: 1, limit: 200 });
        const rows = firstList<any>(data, ['crops', 'data', 'rows']);
        return buildCropOptions(rows.map((row) => row.crop_type || row.product_type || row.name)).map((item) => item.label);
    } catch {
        return [];
    }
};

// ---------------------------------------------------------------------------
// Material lots (compost / manure / mineral fertiliser stock)
// ---------------------------------------------------------------------------
export interface MaterialLot {
    id: number;
    lot_code: string;
    source_type: 'farm_produced' | 'purchased';
    product_type: 'compost' | 'manure' | 'mineral_fertilizer' | 'other';
    product_name: string;
    unit: string;
    supplier_name?: string | null;
    batch_code?: string | null;
    compost_batch_number?: string | null;
    compost_batch_status?: string | null;
    received_quantity: number;
    available_quantity: number;
    issued_quantity: number;
    reserved_quantity: number;
    free_quantity: number;
    /** Derived from the ONE linked expense; 'unpaid' means no cash was deducted. */
    payment_status: 'unpaid' | 'partial' | 'paid' | 'reversed';
    expense_id?: number | null;
    expense_review_status?: 'pending' | 'reviewed' | 'disputed' | null;
    review_assigned_to_user_id?: number | null;
    expense_reviewer_name?: string | null;
    expense_amount?: number | null;
    expense_paid_amount?: number | null;
    expense_currency?: string | null;
    delivery_id?: number | null;
    delivery_status?: string | null;
    received_on?: string | null;
    expiry_date?: string | null;
    quality_notes?: string | null;
    field_restriction_id?: number | null;
    status: string;
    document_urls?: string[];
    photo_urls?: string[];
}

export interface MaterialStockSummaryRow {
    product_type: string;
    source_type: string;
    lot_count: number;
    available_quantity: number;
    issued_quantity: number;
    reserved_quantity: number;
    free_quantity: number;
}

export interface MaterialVendor {
    id: number;
    display_name: string;
    legal_name?: string | null;
    phone?: string | null;
    email?: string | null;
    location?: string | null;
    product_categories: string[];
    is_archived: boolean;
}

export const fetchMaterialVendors = async (): Promise<MaterialVendor[]> => {
    const data = await apiGet<any>('/api/materials/vendors');
    return data?.vendors || [];
};

export const createMaterialVendor = async (payload: AnyRecord): Promise<MaterialVendor> => {
    const data = await apiPost<any>('/api/materials/vendors', payload);
    return data?.vendor;
};

export const fetchMaterialLots = async (params?: AnyRecord): Promise<MaterialLot[]> => {
    const data = await apiGet<any>('/api/materials/lots', params);
    return data?.lots || [];
};

export const fetchMaterialSummary = async (): Promise<MaterialStockSummaryRow[]> => {
    const data = await apiGet<any>('/api/materials/summary');
    return data?.summary || [];
};

export const fetchMaterialLot = async (id: number): Promise<{ lot: MaterialLot; movements: AnyRecord[] }> => {
    const data = await apiGet<any>(`/api/materials/lots/${id}`);
    return { lot: data?.lot, movements: data?.movements || [] };
};

export const createMaterialLot = async (payload: AnyRecord): Promise<any> => {
    return apiPost<any>('/api/materials/lots', payload);
};

/**
 * The one entry point that starts the chain: the purchase's single expense and
 * the material lot it bought are written together, so a stock record can never
 * exist without the cash row that paid for it (or with two of them).
 */
export const recordMaterialPurchase = async (payload: AnyRecord, idempotencyKey?: string): Promise<any> => {
    const init = idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined;
    return apiPost<any>('/api/materials/purchases', payload, init);
};

export const recordExpensePayment = async (expenseId: number, payload: AnyRecord, idempotencyKey?: string): Promise<any> => {
    const init = idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : undefined;
    return apiPost<any>(`/api/owner/money/expenses/${expenseId}/payments`, payload, init);
};

export const fetchExpensePayments = async (expenseId: number): Promise<AnyRecord[]> => {
    const data = await apiGet<any>(`/api/owner/money/expenses/${expenseId}/payments`);
    return data?.payments || [];
};

export const fetchExpenseReviewers = async (): Promise<Array<{ id: number; full_name: string; role: string }>> => {
    const data = await apiGet<any>('/api/owner/money/reviewers');
    return data?.reviewers || [];
};

export const assignExpenseReviewer = async (expenseId: number, reviewerUserId: number | null): Promise<any> =>
    apiPost<any>(`/api/owner/money/expenses/${expenseId}/reviewer`, { reviewer_user_id: reviewerUserId });

/** Undo an approved report's stock application; the report returns to review. */
export const reverseReportStock = async (id: number, reason: string): Promise<any> => {
    return apiPost<any>(`/api/activities/reports/${id}/reverse`, { reason });
};

export const recordMaterialReceipt = async (lotId: number, payload: AnyRecord): Promise<any> => {
    return apiPost<any>(`/api/materials/lots/${lotId}/receipt`, payload);
};

export const issueMaterialForAssignment = async (assignmentId: number, payload: AnyRecord): Promise<any> => {
    return apiPost<any>(`/api/materials/assignments/${assignmentId}/issue`, payload);
};

export const fetchCompostMaterials = async (): Promise<ActivityMaterialOption[]> => {
    const [batchesData, sourcesData] = await Promise.allSettled([
        apiGet<any>('/api/compost/batches/detailed'),
        apiGet<any>('/api/compost/material-sources'),
    ]);

    const batches = batchesData.status === 'fulfilled' ? firstList<any>(batchesData.value, ['batches', 'data', 'rows']) : [];
    const sources = sourcesData.status === 'fulfilled' ? firstList<any>(sourcesData.value, ['sources', 'material_sources', 'data', 'rows']) : [];

    const batchOptions = batches
        .map((batch: any): ActivityMaterialOption | null => {
            const quantity = Number(batch.quantity_kg || batch.kgProduced || batch.actual_output_kg || 0);
            const name = normalizeCropName(batch.product_name || batch.material_name || 'Compost') || 'Compost';
            const code = batch.batch_number || batch.batchNumber || `Batch ${batch.id}`;
            if (!batch.id && !code) return null;
            return {
                id: `batch:${batch.id || code}`,
                name: `${name} - ${code}`,
                unit: 'kg',
                availableQuantity: Number.isFinite(quantity) ? quantity : undefined,
                source: 'compost_batch',
                status: batch.status,
                subtitle: [batch.status, quantity ? `${quantity.toLocaleString()} kg` : null].filter(Boolean).join(' • '),
            };
        })
        .filter(Boolean) as ActivityMaterialOption[];

    const sourceOptions = sources
        .map((source: any): ActivityMaterialOption | null => {
            const quantity = Number(source.quantity_kg || source.quantity || 0);
            const materialName = String(source.material_type || source.material || source.name || '').trim();
            if (!materialName) return null;
            return {
                id: `source:${source.id || materialName}`,
                name: materialName,
                unit: 'kg',
                availableQuantity: Number.isFinite(quantity) ? quantity : undefined,
                source: 'compost_source',
                subtitle: [source.supplier_name, quantity ? `${quantity.toLocaleString()} kg` : null].filter(Boolean).join(' • '),
            };
        })
        .filter(Boolean) as ActivityMaterialOption[];

    return [...batchOptions, ...sourceOptions].sort((a, b) => a.name.localeCompare(b.name));
};

// ---------------------------------------------------------------------------
// Export downloads (raw fetch so we can stream files / blobs)
// ---------------------------------------------------------------------------
const downloadBlob = async (endpoint: string, filename: string) => {
    const token = getAuthToken();
    const response = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error?.error || error?.message || `Download failed (${response.status})`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
};

const openBlob = async (endpoint: string) => {
    const token = getAuthToken();
    const response = await fetch(`${API_URL}${endpoint}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: response.statusText }));
        throw new Error(error?.error || error?.message || `Open failed (${response.status})`);
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, '_blank', 'noopener,noreferrer');
    setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
};

export const downloadAssignmentJson = async (id: number) => {
    const code = (await apiGet<any>(`/api/activities/assignments/${id}`))?.assignment?.assignment_code || `assignment-${id}`;
    await downloadBlob(`/api/activities/assignments/${id}/export.json`, `${code}.json`);
};

export const downloadReportJson = async (report: ActivityReport) => {
    await downloadBlob(`/api/activities/reports/${report.id}/export.json`, `${report.report_code || `report-${report.id}`}.json`);
};

export const downloadReportCsv = async (report: ActivityReport) => {
    await downloadBlob(`/api/activities/reports/${report.id}/export.csv`, `${report.report_code || `report-${report.id}`}.csv`);
};

export const openReportPrintable = (report: ActivityReport) => {
    return openBlob(`/api/activities/reports/${report.id}/export.html`);
};

export { emptyList, firstList };
